/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving timepoints.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialTimepointsState {
    timepoints: Dtos.Timepoint[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface ITimepointsState {
    timepoints: IInitialTimepointsState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const TimepointsInitialState: IInitialTimepointsState = {
    timepoints: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useTimepointsSelector: TypedUseSelectorHook<ITimepointsState> = useSelector;

export const TimepointsSelectors = {
    selectTimepoints: (state: ITimepointsState) => state.timepoints.timepoints,
    selectLoadState: (state: ITimepointsState) => state.timepoints.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class TimepointsReducer extends ImmerReducer<IInitialTimepointsState> {
    public load() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadByTreatmentId(treatmentId: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(timepoints?: Dtos.Timepoint[]) {
        this.draftState.timepoints = timepoints ? timepoints : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.timepoints = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...TimepointsInitialState };
    }
};

export const TimepointsActions = createActionCreators(TimepointsReducer);
const reducer = createReducerFunction(TimepointsReducer, TimepointsInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const TimepointsApi = {
    load: () => {
        return client.get(new Dtos.GetTimepoints());
    },
    loadByTreatmentId: (treatmentId: number) => {
        return client.get(new Dtos.GetTimepointsByTreatmentId({
            treatmentId
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const TimepointsLogic = {
    load: createLogic<ITimepointsState, {}, undefined, string, ReturnType<typeof TimepointsActions.load>>({
        type: TimepointsActions.load.type,
        process: async ({ action }, dispatch, done) => {
            try {
                const response = await TimepointsApi.load();

                dispatch(TimepointsActions.loadSuccess(
                    response.timepoints
                ));
            }
            catch (error: any) {
                dispatch(TimepointsActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    loadByTreatmentId: createLogic<ITimepointsState, {}, undefined, string, ReturnType<typeof TimepointsActions.loadByTreatmentId>>({
        type: TimepointsActions.loadByTreatmentId.type,
        process: async ({ action }, dispatch, done) => {
            const treatmentId = action.payload;

            try {
                const response = await TimepointsApi.loadByTreatmentId(treatmentId);

                dispatch(TimepointsActions.loadSuccess(
                    response.timepoints
                ));
            }
            catch (error: any) {
                dispatch(TimepointsActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    TimepointsLogic.load,
    TimepointsLogic.loadByTreatmentId
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useTimepoints = (): [Dtos.Timepoint[] | null, IRequestState<Dtos.ResponseStatus>, typeof TimepointsActions] => {
    const timepoints = useSelector(TimepointsSelectors.selectTimepoints);
    const loadState = useSelector(TimepointsSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof TimepointsActions>(() => {
        return bindActionCreators(TimepointsActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(TimepointsActions, dispatch));
    }, [dispatch, setActions]);

    return [
        timepoints,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('timepoints', reducer as any, logics as any);